/**
 * frontend/src/pages/Settings.js
 *
 * Workspace settings management page.
 * Allows workspace admins to update the workspace name, invite members via email,
 * and control team member roles or remove members.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Alert, Modal } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  FiSettings, FiUsers, FiMail, FiSend,
  FiShield, FiTrash2, FiX, FiArrowLeft, FiClock
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { formatErrorMessage } from '../utils/errorHandling';
import Sidebar from '../components/Layout/Sidebar';
import Header from '../components/Layout/Header';

const pageStyles = {
  pageWrapper: {
    minHeight: '100vh',
    background: '#f8fafc',
  },
  mainContent: {
    marginLeft: '72px',
    marginTop: '64px',
    minHeight: 'calc(100vh - 64px)',
    padding: '32px 48px',
  },
  contentWrapper: {
    maxWidth: '1200px',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#64748b',
    textDecoration: 'none',
    fontSize: '0.9rem',
    marginBottom: '24px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  pageTitle: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  pageSubtitle: {
    color: '#64748b',
    marginBottom: '32px',
  },
  sectionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
  },
  section: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #e2e8f0',
  },
  sectionFull: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #e2e8f0',
    gridColumn: '1 / -1',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  inviteInput: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  },
  inviteTextarea: {
    flex: 1,
    minHeight: '80px',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  emailTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: '#f1f5f9',
    borderRadius: '6px',
    fontSize: '0.85rem',
    color: '#475569',
    margin: '4px',
  },
  pendingInvite: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: '#fefce8',
    borderRadius: '8px',
    marginBottom: '8px',
    border: '1px solid #fef08a',
  },
  memberItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #f1f5f9',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: '600',
    fontSize: '1rem',
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 64px)',
  },
  resultBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
  },
};

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    currentWorkspace,
    members,
    invites,
    fetchWorkspace,
    fetchMembers,
    fetchWorkspaces,
    fetchInvites,
    updateWorkspace,
    updateMemberRole,
    removeMember,
    inviteMembers,
    cancelInvite,
  } = useWorkspace();

  const [workspaceName, setWorkspaceName] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        const workspaces = await fetchWorkspaces();
        if (workspaces && workspaces.length > 0) {
          workspaceId = workspaces[0].id;
        } else {
          navigate('/dashboard');
          return;
        }
      }
      const workspace = await fetchWorkspace(workspaceId);
      setWorkspaceName(workspace.name);
      await Promise.all([
        fetchMembers(workspaceId),
        fetchInvites(workspaceId)
      ]);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load workspace settings');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id, fetchWorkspace, fetchMembers, fetchInvites, fetchWorkspaces, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (currentWorkspace && currentWorkspace.user_role !== 'admin') {
      navigate('/dashboard');
    }
  }, [currentWorkspace, navigate]);

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!currentWorkspace?.id) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateWorkspace(currentWorkspace.id, { name: workspaceName });
      setSuccess('Workspace name updated successfully');
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to update workspace name'));
    } finally {
      setSaving(false);
    }
  };

  const parseEmails = (input) => {
    return input
      .split(/[,\n]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email && email.includes('@'));
  };

  const handleInvite = async () => {
    const emails = parseEmails(emailInput);
    if (emails.length === 0) {
      setError('Please enter at least one valid email address');
      return;
    }

    if (!currentWorkspace?.id) return;
    setInviting(true);
    setError('');
    setSuccess('');
    setInviteResult(null);

    try {
      const result = await inviteMembers(currentWorkspace.id, emails);
      setInviteResult(result);
      if (result.sent.length > 0) {
        setSuccess(`Invited ${result.sent.length} member${result.sent.length > 1 ? 's' : ''} successfully`);
        setEmailInput('');
        await fetchInvites(currentWorkspace.id);
      }
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to send invitations'));
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!currentWorkspace?.id) return;
    try {
      await cancelInvite(currentWorkspace.id, inviteId);
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to cancel invitation'));
    }
  };

  const handleRoleChange = async (memberId, userId, newRole) => {
    if (!currentWorkspace?.id) return;
    setError('');
    setSuccess('');
    try {
      await updateMemberRole(currentWorkspace.id, userId, newRole);
      setSuccess('Member role updated successfully');
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to update member role'));
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove || !currentWorkspace?.id) return;
    setError('');
    setSuccess('');
    try {
      await removeMember(currentWorkspace.id, memberToRemove.user_id);
      setSuccess('Member removed successfully');
      setShowRemoveModal(false);
      setMemberToRemove(null);
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to remove member'));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={pageStyles.pageWrapper}>
        <Sidebar workspace={currentWorkspace} />
        <Header workspace={currentWorkspace} />
        <main style={{ ...pageStyles.mainContent, ...pageStyles.loadingWrapper }}>
          <div className="spinner" style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </main>
      </div>
    );
  }

  return (
    <div style={pageStyles.pageWrapper}>
      <Sidebar workspace={currentWorkspace} />
      <Header workspace={currentWorkspace} />

      <main style={pageStyles.mainContent}>
        <div style={pageStyles.contentWrapper}>
          <motion.button
            style={pageStyles.backButton}
            onClick={() => navigate('/dashboard')}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ color: '#1e293b' }}
          >
            <FiArrowLeft />
            Back to Dashboard
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 style={pageStyles.pageTitle}>
              <FiSettings />
              Workspace Settings
            </h1>
            <p style={pageStyles.pageSubtitle}>Manage your workspace settings and team members</p>
          </motion.div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <div style={pageStyles.sectionsGrid}>
            {/* General Settings */}
            <motion.div
              style={pageStyles.section}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h5 style={pageStyles.sectionTitle}>
                <FiSettings />
                General Settings
              </h5>
              <Form onSubmit={handleSaveName}>
                <Form.Group className="mb-3">
                  <Form.Label>Workspace Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    required
                  />
                </Form.Group>
                <Button type="submit" style={{ background: '#6366f1', border: 'none' }} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Form>
            </motion.div>

            {/* Invite Members */}
            <motion.div
              style={pageStyles.section}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h5 style={pageStyles.sectionTitle}>
                <FiMail />
                Invite Members
              </h5>
              <p className="text-muted small mb-3">
                Enter email addresses separated by commas or new lines
              </p>
              <div style={pageStyles.inviteInput}>
                <Form.Control
                  as="textarea"
                  style={pageStyles.inviteTextarea}
                  placeholder="email@example.com, another@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                />
                <Button
                  style={{ background: '#6366f1', border: 'none', height: '42px' }}
                  onClick={handleInvite}
                  disabled={inviting || !emailInput.trim()}
                >
                  {inviting ? '...' : <FiSend />}
                </Button>
              </div>

              {inviteResult && (
                <div className="mt-3">
                  {inviteResult.already_member.length > 0 && (
                    <div className="mb-2">
                      <span style={{ ...pageStyles.resultBadge, background: '#dbeafe', color: '#1e40af' }}>
                        Already members:
                      </span>
                      {inviteResult.already_member.map(email => (
                        <span key={email} style={pageStyles.emailTag}>{email}</span>
                      ))}
                    </div>
                  )}
                  {inviteResult.already_invited.length > 0 && (
                    <div>
                      <span style={{ ...pageStyles.resultBadge, background: '#fef3c7', color: '#92400e' }}>
                        Already invited:
                      </span>
                      {inviteResult.already_invited.map(email => (
                        <span key={email} style={pageStyles.emailTag}>{email}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>

            {/* Pending Invites */}
            {invites && invites.length > 0 && (
              <motion.div
                style={pageStyles.sectionFull}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <h5 style={pageStyles.sectionTitle}>
                  <FiClock />
                  Pending Invitations ({invites.length})
                </h5>
                <div>
                  {invites.map((invite) => (
                    <div key={invite.id} style={pageStyles.pendingInvite}>
                      <div className="d-flex align-items-center gap-3">
                        <FiMail style={{ color: '#ca8a04' }} />
                        <div>
                          <div className="fw-medium">{invite.email}</div>
                          <div className="small text-muted">
                            Invited {formatDate(invite.created_at)}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={() => handleCancelInvite(invite.id)}
                        title="Cancel invitation"
                      >
                        <FiX />
                      </Button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Team Members */}
            <motion.div
              style={pageStyles.sectionFull}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h5 style={pageStyles.sectionTitle}>
                <FiUsers />
                Team Members ({members.length})
              </h5>

              <div>
                {members.map((member) => (
                  <div key={member.id} style={pageStyles.memberItem}>
                    <div className="d-flex align-items-center gap-3">
                      <div style={pageStyles.avatar}>
                        {member.user_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="fw-semibold">
                          {member.user_name}
                          {member.user_id === user?.id && (
                            <span className="text-muted small ms-2">(You)</span>
                          )}
                        </div>
                        <div className="small text-muted">{member.user_email}</div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      {member.user_id !== user?.id ? (
                        <>
                          <Form.Select
                            size="sm"
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.id, member.user_id, e.target.value)}
                            style={{ width: 'auto' }}
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </Form.Select>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              setMemberToRemove(member);
                              setShowRemoveModal(true);
                            }}
                          >
                            <FiTrash2 />
                          </Button>
                        </>
                      ) : (
                        <span className="badge" style={{ background: '#6366f1' }}>
                          <FiShield className="me-1" />
                          {member.role}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Remove Member Modal */}
      <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Member</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove <strong>{memberToRemove?.user_name}</strong> from this workspace?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRemoveMember}>
            Remove Member
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Settings;
